import { ThemeProvider } from '@mui/system';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import Router from './Router';
import { defaultTheme } from './Themes';
import { jwtDecode } from 'jwt-decode';
import type { AccessTokenData } from './utils/getUserRolesFromToken';
import { environment } from './utils/environment';
import { initAnalytics } from './utils/analytics';

const MAX_ADVANCED_MATCHING_RETRIES = 15;
const ADVANCED_MATCHING_RETRY_DELAY_MS = 2000;

declare global {
  interface Window {
    fbqDebug?: () => {
      cookies: string;
      hasCookieConsent: boolean;
      pixelId: string;
      fbqAvailable: boolean;
    };
    fbqAdvancedMatchingRetryHandle?: number;
  }
}

const hasCookieConsent =
  typeof document !== 'undefined' &&
  document.cookie.includes('catvanteAnalyticsCookiesAccepted=true');

const facebookPixelId = environment.facebook.pixelId;
const isBrowser = typeof window !== 'undefined';
const isFbqAvailable = isBrowser && typeof window.fbq === 'function';

if (isBrowser) {
  window.fbqDebug =
    window.fbqDebug ??
    (() => ({
      cookies: document.cookie,
      hasCookieConsent,
      pixelId: facebookPixelId,
      fbqAvailable: isFbqAvailable,
    }));
}

const getExternalIdFromToken = (logErrors = false): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const token = localStorage.getItem('AccessToken');
  if (!token) {
    return undefined;
  }

  try {
    const payload = jwtDecode<AccessTokenData>(token);
    const externalId = payload?.uid;
    return externalId ? String(externalId) : undefined;
  } catch (err) {
    if (logErrors) {
      console.warn(
        '[Pixel] Decodificação do AccessToken para Advanced Matching falhou:',
        err
      );
    }
    return undefined;
  }
};

const initPixelWithAdvancedMatching = (externalId?: string) => {
  if (!window.fbq) {
    return;
  }

  if (externalId) {
    window.fbq('init', facebookPixelId, { external_id: externalId });
  } else {
    window.fbq('init', facebookPixelId);
  }
};

const scheduleAdvancedMatchingRetry = () => {
  if (!window.fbq) {
    return;
  }

  if (typeof window.fbqAdvancedMatchingRetryHandle === 'number') {
    window.clearInterval(window.fbqAdvancedMatchingRetryHandle);
    window.fbqAdvancedMatchingRetryHandle = undefined;
  }

  let attempts = 0;
  const handle = window.setInterval(() => {
    attempts += 1;
    const retryExternalId = getExternalIdFromToken();

    if (retryExternalId && window.fbq) {
      console.info('[Pixel] Advanced Matching atualizado após tentativa', attempts);
      initPixelWithAdvancedMatching(retryExternalId);
      window.clearInterval(handle);
      window.fbqAdvancedMatchingRetryHandle = undefined;
    } else if (attempts >= MAX_ADVANCED_MATCHING_RETRIES) {
      console.warn(
        '[Pixel] Advanced Matching não disponível após múltiplas tentativas. Prosseguindo sem external_id'
      );
      window.clearInterval(handle);
      window.fbqAdvancedMatchingRetryHandle = undefined;
    }
  }, ADVANCED_MATCHING_RETRY_DELAY_MS);

  window.fbqAdvancedMatchingRetryHandle = handle;
};

const pixelPreconditionsOk = hasCookieConsent && isFbqAvailable && facebookPixelId;

if (pixelPreconditionsOk && window.fbq) {
  // Consent and init - Pixel Advanced Matching
  window.fbq('consent', 'grant');
  console.info('[Pixel] Consent granted and initialization started');

  const externalId = getExternalIdFromToken(true);
  initPixelWithAdvancedMatching(externalId);

  if (!externalId) {
    scheduleAdvancedMatchingRetry();
  }
} else {
  console.warn('[Pixel] Pré-condições não atendidas. Firing abortado');
}

if (isBrowser && hasCookieConsent) {
  const gaInitialized = initAnalytics();
  if (gaInitialized) {
    console.info('[GA] Consent granted and initialization started');
  } else {
    console.warn('[GA] Pré-condições não atendidas ou Measurement ID ausente');
  }
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.Fragment>
    <ThemeProvider theme={defaultTheme}>
      <Router />
    </ThemeProvider>
  </React.Fragment>
);
